import { BfClientType, BfPaymentProvider, BfPlanTypes, BfUser } from "@/lib/api/models";
import { DEBUG_MODE, getClientSimple, isAndroidApp, isCapacitor, isiOSApp } from "@/lib/client";
import { Friendly } from "@/lib/friendly";
import { Toast } from "@/lib/toast";
import {
    LOG_LEVEL,
    PurchasesPackage,
    PurchasesStoreProduct,
    CustomerInfo as RcCapCustomerInfo,
    Purchases as RcCapPurchases,
} from "@revenuecat/purchases-capacitor";
import {
    Package,
    Product,
    CustomerInfo as RcWebCustomerInfo,
    Purchases as RcWebPurchases,
} from "@revenuecat/purchases-js";
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useAppContext } from "./AppContext";

type SupportedMap = {
    platforms: BfClientType[];
    planType: BfPlanTypes;
};

const SUPPORTED: SupportedMap[] = [
    {
        platforms: [BfClientType.web, BfClientType.android, BfClientType.ios, BfClientType.pwatwa],
        planType: BfPlanTypes.pro,
    },
    {
        platforms: [BfClientType.web, BfClientType.android, BfClientType.ios, BfClientType.pwatwa],
        planType: BfPlanTypes.business,
    },
];

export interface IPaymentContext {
    login: (user: BfUser) => Promise<void>;
    customerInfo: () => Promise<RcCapCustomerInfo | RcWebCustomerInfo>;
    productMonthly: (id: BfPlanTypes) => Promise<CommonPackage>;
    productAnnual: (id: BfPlanTypes) => Promise<CommonPackage>;
    purchase: (pkg: CommonPackage) => Promise<void>;
    isUserSubscribed: (id: BfPlanTypes) => Promise<boolean>;
    isPaymentSupported: () => boolean;
    isUserSubscribedAny: (ids: BfPlanTypes[]) => Promise<boolean>;
    synchronisePurchases: () => Promise<void>;
    paymentUpdate: number;
    restorePurchases: () => Promise<void>;
}

const PaymentContext = createContext<IPaymentContext | undefined>(undefined);

let isRcInitialised = false;

interface PaymentProviderProps {
    children: ReactNode;
}

export const PaymentProvider = ({ children }: PaymentProviderProps) => {
    const { user, isLoggedIn, userApi } = useAppContext();
    const [paymentUpdate, setPaymentUpdate] = useState<number>(0);

    const isPaymentSupported = useCallback(() => {
        return isRcInitialised;
    }, []);

    const customerInfo = () => {
        if (!isPaymentSupported() || !isLoggedIn) {
            return Promise.reject("PaymentContext not supported or user not logged in");
        }

        return isCapacitor()
            ? RcCapPurchases.getCustomerInfo().then((ci) => {
                  return ci.customerInfo;
              })
            : RcWebPurchases.getSharedInstance().getCustomerInfo();
    };

    const productMonthly = async (id: BfPlanTypes): Promise<CommonPackage> => {
        if (!isPaymentSupported()) {
            return Promise.reject("PaymentContext not supported or user not logged in");
        }
        return isCapacitor()
            ? RcCapPurchases.getOfferings().then((offerings) => {
                  return new CommonPackage(id, offerings.all[id]?.monthly);
              })
            : RcWebPurchases.getSharedInstance()
                  .getOfferings()
                  .then((offerings) => {
                      return new CommonPackage(id, null, offerings.all[id]?.monthly);
                  });
    };

    const productAnnual = async (id: BfPlanTypes): Promise<CommonPackage> => {
        if (!isPaymentSupported()) {
            return Promise.reject("PaymentContext not supported or user not logged in");
        }
        return isCapacitor()
            ? RcCapPurchases.getOfferings().then((offerings) => {
                  return new CommonPackage(id, offerings.all[id]?.annual);
              })
            : RcWebPurchases.getSharedInstance()
                  .getOfferings()
                  .then((offerings) => {
                      return new CommonPackage(id, null, offerings.all[id]?.annual);
                  });
    };

    const purchase = (pkg: CommonPackage): Promise<void> => {
        if (!isPaymentSupported() || !isLoggedIn || !user) {
            return Promise.reject("PaymentContext not supported or user not logged in");
        }

        console.log("PaymentContext Purchasing package", pkg);

        if (isCapacitor() && pkg.capacitorPackage) {
            return RcCapPurchases.purchasePackage({ aPackage: pkg.capacitorPackage }).then(
                (result) => {
                    console.log("PaymentContext purchase result", result);
                    return userApi.validateRevenuecatSubscription().then((d) => {
                        return d.data;
                    });
                }
            );
        } else if (pkg.webPackage) {
            return RcWebPurchases.getSharedInstance()
                .purchase({
                    rcPackage: pkg.webPackage,
                    customerEmail: user.email,
                })
                .then((result) => {
                    console.log("PaymentContext purchase result", result);
                    return userApi.validateRevenuecatSubscription().then((d) => {
                        return d.data;
                    });
                });
        } else {
            return Promise.reject("PaymentContext No purchase package found");
        }
    };

    const isUserSubscribed = (id: BfPlanTypes): Promise<boolean> => {
        if (!isPaymentSupported() || !isLoggedIn) {
            return Promise.resolve(false);
        }

        return customerInfo()
            .then((info) => {
                const isSubscribed = info.entitlements.active[id] !== undefined;

                if (DEBUG_MODE) {
                    console.log("PaymentContext User subscribed", id, isSubscribed);
                }

                return isSubscribed;
            })
            .catch((e) => {
                console.warn("PaymentContext Failed to check if user is subscribed", e);
                return false;
            });
    };

    const isUserSubscribedAny = (ids: BfPlanTypes[]): Promise<boolean> => {
        if (!isPaymentSupported() || !isLoggedIn) {
            return Promise.reject("PaymentContext not supported or user not logged in");
        }

        return customerInfo()
            .then((info) => {
                return ids.some((id) => info.entitlements.active[id] !== undefined);
            })
            .catch((e) => {
                console.warn("PaymentContext Failed to check if user is subscribed", e);
                return false;
            });
    };

    const synchronisePurchases = useCallback(async () => {
        if (!isPaymentSupported() || !isLoggedIn) {
            return Promise.reject("PaymentContext not supported or user not logged in");
        }

        if (isCapacitor()) {
            return RcCapPurchases.syncPurchases();
        } else {
            return Promise.resolve();
        }
    }, [isPaymentSupported, isLoggedIn]);

    // Perform revenuecat initialisation
    const init = useCallback(async () => {
        // Ensure payment is supported and RC hasn't been initialised
        if (isRcInitialised) {
            return;
        }

        // Determine the user ID
        const userId = user ? user.userId : undefined;

        if (DEBUG_MODE) console.log("PaymentContext initialising with userid", userId);

        // iOS and Android apps use capacitor and are initialised differently.
        // Annoyingly Web is initialised differently and from another package.
        if (isiOSApp()) {
            isRcInitialised = true;
            await RcCapPurchases.setLogLevel({
                level: DEBUG_MODE ? LOG_LEVEL.DEBUG : LOG_LEVEL.INFO,
            });
            await RcCapPurchases.configure({
                apiKey: import.meta.env.VITE_CLIENT_REVENUECAT_IOS_KEY,
                appUserID: userId,
            });
            await RcCapPurchases.addCustomerInfoUpdateListener((customerInfo) => {
                if (DEBUG_MODE) console.log("PaymentContext customer info updated", customerInfo);
                setPaymentUpdate((prev) => prev + 1);
            });
            if (DEBUG_MODE) console.log("PaymentContext initialised for iOS");
        } else if (isAndroidApp()) {
            isRcInitialised = true;
            await RcCapPurchases.setLogLevel({
                level: DEBUG_MODE ? LOG_LEVEL.DEBUG : LOG_LEVEL.INFO,
            });
            await RcCapPurchases.configure({
                apiKey: import.meta.env.VITE_CLIENT_REVENUECAT_ANDROID_KEY,
                appUserID: userId,
            });
            await RcCapPurchases.addCustomerInfoUpdateListener((customerInfo) => {
                if (DEBUG_MODE) console.log("PaymentContext customer info updated", customerInfo);
                setPaymentUpdate((prev) => prev + 1);
            });
            if (DEBUG_MODE) console.log("PaymentContext initialised for Android");
        } else if (userId) {
            isRcInitialised = true;
            await RcWebPurchases.configure({
                apiKey: import.meta.env.VITE_CLIENT_REVENUECAT_WEB_KEY,
                appUserId: userId,
            });
            if (DEBUG_MODE) console.log("PaymentContext initialised for Web");
        }

        // Check if the subscriptions need to be synced
        if (user) {
            if (
                user.activePaymentProviders &&
                user.activePaymentProviders.length > 0 &&
                !user.activePaymentProviders?.includes(BfPaymentProvider.revenueCat) &&
                !user.activePaymentProviders?.includes(BfPaymentProvider.licence)
            ) {
                synchronisePurchases()
                    .then(() => {
                        console.log("PaymentContext synchronised purchases");
                    })
                    .catch((e) => {
                        console.warn("PaymentContext failed to synchronise purchases", e);
                    });
            }
        }
    }, [synchronisePurchases, user]);

    const login = useCallback(
        async (user: BfUser) => {
            if (isCapacitor()) {
                if (DEBUG_MODE) console.log("PaymentContext logging in user", user.userId);
                RcCapPurchases.logIn({ appUserID: user.userId }).catch((e) => {
                    console.warn("PaymentContext Failed to log in user", e);
                });
            } else if (!isRcInitialised) {
                init().catch((e) => {
                    console.warn("PaymentContext Failed to initialise RevenueCat", e);
                });
            }
        },

        [init]
    );

    const restorePurchases = useCallback(async (): Promise<void> => {
        if (!user) {
            return Promise.reject("No user logged in");
        }

        if (!isCapacitor()) {
            return Promise.reject("Restore purchases is only supported on mobile apps");
        }

        RcCapPurchases.restorePurchases()
            .then((custInfo) => {
                console.log("PaymentContext restored purchases", custInfo);
                return userApi
                    .validateRevenuecatSubscription()
                    .then((d) => {
                        Toast.success("Successfully restored purchases");
                        return d.data;
                    })
                    .catch((e) => {
                        console.warn(
                            "PaymentContext Failed to validate subscription after restore",
                            e
                        );
                        Toast.error(
                            "We restored your purchases, but couldn't validate your subscription. You might need to manually restart your app."
                        );
                        throw e;
                    });
            })
            .catch((e) => {
                console.warn("PaymentContext Failed to restore purchases", e);
                Toast.error(`Failed to restore purchases ${e}`);
                throw e;
            });
    }, [user, userApi]);

    useEffect(() => {
        if (user) {
            login(user);
        }
    }, [user, login]);

    /// Initial initialisation when the app starts
    useEffect(() => {
        init().catch((e) => {
            console.warn("PaymentContext Failed to initialise RevenueCat", e);
        });
    }, [init]);

    const paymentContextValue: IPaymentContext = {
        login,
        customerInfo,
        productMonthly,
        purchase,
        isUserSubscribed,
        isPaymentSupported,
        isUserSubscribedAny,
        synchronisePurchases,
        productAnnual,
        paymentUpdate,
        restorePurchases,
    };

    window.payment = paymentContextValue;

    return (
        <PaymentContext.Provider value={paymentContextValue}>{children}</PaymentContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const usePayment = () => {
    const context = useContext(PaymentContext);
    if (!context) {
        throw new Error("PaymentContext usePayment must be used within a PaymentProvider");
    }
    return context;
};

export class CommonPackage {
    capacitorPackage: PurchasesPackage | null = null;
    webPackage: Package | null = null;
    planType: BfPlanTypes;

    constructor(
        planType: BfPlanTypes,
        capacitorPackage: PurchasesPackage | null = null,
        webPackage: Package | null = null
    ) {
        this.planType = planType;
        this.capacitorPackage = capacitorPackage;
        this.webPackage = webPackage;
    }

    title(): string | undefined {
        return (
            this.capacitorPackage?.product.title ||
            this.webPackage?.webBillingProduct.title ||
            Friendly.PlanType(this.planType)
        );
    }

    loadedPackage(): boolean {
        return this.capacitorPackage !== null || this.webPackage !== null;
    }

    availableOnPlatform(): boolean {
        const platform = getClientSimple();
        let isSupported = false;
        SUPPORTED.forEach((s) => {
            if (s.planType === this.planType && s.platforms.includes(platform)) {
                isSupported = true;
            }
        });
        return isSupported;
    }

    price(): string | undefined {
        return (
            this.capacitorPackage?.product.priceString ||
            this.webPackage?.webBillingProduct.price.formattedPrice ||
            undefined
        );
    }

    package(): PurchasesPackage | Package | undefined {
        return this.capacitorPackage || this.webPackage || undefined;
    }

    product(): Product | PurchasesStoreProduct | undefined {
        return this.capacitorPackage?.product || this.webPackage?.webBillingProduct || undefined;
    }
}
